import { mapGetters } from 'vuex'
import { RESTO_API_BASE_URL } from '@constants/credentials'
import { SITE_SLUG } from '@constants/siteDetails'

// Production builds never set the override, so live screens check every 5 minutes
const CHECK_INTERVAL_MS =
  Number(process.env.VUE_APP_TEST_VERSION_CHECK_MS) || 5 * 60 * 1000
// Ticks fetch nothing. They only notice a wake soon after it happens
export const TICK_MS = Math.min(30 * 1000, CHECK_INTERVAL_MS)
// Timers stop while a device sleeps, so a tick this late means it just woke
export const WAKE_GAP_MS = 15 * 60 * 1000
// Wifi can take a while to come back after a wake, so a display checks on
// every tick for this long
export const WAKE_RETRY_MS = 5 * 60 * 1000
// Local hours, start inclusive, when a display may reload without a wake
export const RELOAD_WINDOW = Object.freeze({ START_HOUR: 3, END_HOUR: 5 })
// Kept across the reload, so a deploy whose reload keeps serving the old build
// (say a stale cached index.html) is only tried this many times
export const MAX_RELOAD_ATTEMPTS = 2
// A cache that served the old build clears eventually, so giving up isn't for
// good. The tries start over after this long
export const RELOAD_ATTEMPT_RESET_MS = 6 * 60 * 60 * 1000
export const RELOAD_ATTEMPT_KEY = 'resto:reload-attempt'
const REQUEST_TIMEOUT_MS = 10 * 1000

export const SCREEN_TYPES = Object.freeze({
  DISPLAY: 'display',
  EDITOR: 'editor',
  PUBLIC: 'public',
})

export const NEW_VERSION_ACTIONS = Object.freeze({
  RELOAD: 'reload',
  WAIT: 'wait',
  TOAST: 'toast',
  IGNORE: 'ignore',
})

// A layout with no editing UI open, logged in or not
export function isDisplayScreen({
  routeName,
  isShowingLayoutEditUI,
  isShowingEditUI,
}) {
  return routeName === 'layout' && !isShowingLayoutEditUI && !isShowingEditUI
}

// A layout refresh replaces cached sections, which would drop unpublished edits
export function shouldPollLayout({
  loggedIn,
  hasUnpublishedChanges,
  ...screen
}) {
  if (!loggedIn) return true
  return !hasUnpublishedChanges && isDisplayScreen(screen)
}

export function screenTypeFor({ loggedIn, ...screen }) {
  if (isDisplayScreen(screen)) return SCREEN_TYPES.DISPLAY
  return loggedIn ? SCREEN_TYPES.EDITOR : SCREEN_TYPES.PUBLIC
}

// A reload would drop a display's unpublished changes, so it waits until
// they're published
export function newVersionActionFor({ screenType, hasUnpublishedChanges }) {
  switch (screenType) {
    case SCREEN_TYPES.DISPLAY:
      return hasUnpublishedChanges
        ? NEW_VERSION_ACTIONS.WAIT
        : NEW_VERSION_ACTIONS.RELOAD
    case SCREEN_TYPES.EDITOR:
      return NEW_VERSION_ACTIONS.TOAST
    default:
      return NEW_VERSION_ACTIONS.IGNORE
  }
}

export function isInReloadWindow(date) {
  const hour = date.getHours()
  return hour >= RELOAD_WINDOW.START_HOUR && hour < RELOAD_WINDOW.END_HOUR
}

export function hasWoken({ lastTickAt, now }) {
  return now - lastTickAt - TICK_MS > WAKE_GAP_MS
}

export function isAfterWake({ now, wokeAt }) {
  return wokeAt !== null && now >= wokeAt && now - wokeAt < WAKE_RETRY_MS
}

// A tab the browser restores after a restart comes from its cache without
// asking the server, so it runs the old build. To a display that's a wake
export function isRestoredPageLoad(perf = window.performance) {
  if (!perf) return false
  const [entry] = perf.getEntriesByType
    ? perf.getEntriesByType('navigation')
    : []
  if (entry) return entry.type === 'back_forward'
  return !!perf.navigation && perf.navigation.type === 2
}

// A reload blanks the screen, so a display only takes one when nobody is
// likely watching: overnight, or just after the device woke. A forced reload
// goes out at any hour
export function canReloadDisplay({ now, wokeAt, forceReload = false }) {
  return (
    forceReload ||
    isInReloadWindow(new Date(now)) ||
    isAfterWake({ now, wokeAt })
  )
}

// Different rather than higher, so publishing an older build also goes out
// right away. A retried deploy has the same number, so it waits as usual
export function isForceReload({ running, published }) {
  return (
    Number.isInteger(running) &&
    Number.isInteger(published) &&
    running !== published
  )
}

// Netlify answers a missing file with index.html, so anything that isn't
// JSON with a deploy ID means there's nothing new
export function parsePublishedVersion(body) {
  try {
    const { deployId, forceReloadVersion } = JSON.parse(body)
    if (typeof deployId !== 'string' || !deployId.length) return null
    return {
      deployId,
      forceReloadVersion: Number.isInteger(forceReloadVersion)
        ? forceReloadVersion
        : null,
    }
  } catch (e) {
    return null
  }
}

function readReloadAttempt() {
  try {
    const attempt = JSON.parse(
      window.sessionStorage.getItem(RELOAD_ATTEMPT_KEY)
    )
    return attempt && typeof attempt.deployId === 'string' ? attempt : null
  } catch (e) {
    return null
  }
}

function writeReloadAttempt(attempt) {
  try {
    window.sessionStorage.setItem(RELOAD_ATTEMPT_KEY, JSON.stringify(attempt))
    return true
  } catch (e) {
    return false
  }
}

function clearReloadAttempt() {
  try {
    window.sessionStorage.removeItem(RELOAD_ATTEMPT_KEY)
  } catch (e) {}
}

// A request left hanging by a wifi drop would hold up every later check.
// Browsers without AbortController still give up, though the request lingers
async function fetchFresh(url, read) {
  const controller = window.AbortController ? new AbortController() : null
  let timeout
  const timedOut = new Promise((resolve, reject) => {
    timeout = setTimeout(() => {
      if (controller) controller.abort()
      reject(new Error(`${url} timed out`))
    }, REQUEST_TIMEOUT_MS)
  })
  try {
    return await Promise.race([
      fetch(url, {
        cache: 'no-store',
        signal: controller ? controller.signal : undefined,
      }).then(read),
      timedOut,
    ])
  } finally {
    clearTimeout(timeout)
  }
}

// The reloaded page would only show an error if the API can't be reached
async function apiAnswers() {
  try {
    return await fetchFresh(
      `${RESTO_API_BASE_URL}/path-to-model/${SITE_SLUG}`,
      (response) => response.ok
    )
  } catch (e) {
    return false
  }
}

export default {
  data() {
    return {
      runningDeployId: process.env.VUE_APP_DEPLOY_ID,
      publishedDeployId: null,
      runningForceReloadVersion: Number(
        process.env.VUE_APP_FORCE_RELOAD_VERSION
      ),
      publishedForceReloadVersion: null,
      lastTickAt: null,
      lastCheckAt: null,
      wokeAt: null,
      isCheckingVersion: false,
      versionTickInterval: null,
    }
  },
  computed: {
    ...mapGetters('auth', ['loggedIn']),
    ...mapGetters('layoutEditor', ['isShowingLayoutEditUI']),
    ...mapGetters('editor', ['isShowingEditUI', 'hasUnpublishedChanges']),
    newVersionAction() {
      if (
        !this.publishedDeployId ||
        this.publishedDeployId === this.runningDeployId
      ) {
        return NEW_VERSION_ACTIONS.IGNORE
      }
      return newVersionActionFor({
        screenType: screenTypeFor({
          routeName: this.$route.name,
          loggedIn: this.loggedIn,
          isShowingLayoutEditUI: this.isShowingLayoutEditUI,
          isShowingEditUI: this.isShowingEditUI,
        }),
        hasUnpublishedChanges: this.hasUnpublishedChanges,
      })
    },
    isShowingNewVersionToast() {
      return this.newVersionAction === NEW_VERSION_ACTIONS.TOAST
    },
  },
  mounted() {
    if (!this.runningDeployId) return
    const now = Date.now()
    const attempt = readReloadAttempt()
    if (attempt && attempt.deployId === this.runningDeployId) {
      clearReloadAttempt()
    } else if (attempt && isAfterWake({ now, wokeAt: attempt.at })) {
      // Just after its own reload, so the old build came back. Try again now
      this.wokeAt = attempt.at
    }
    if (isRestoredPageLoad()) this.wokeAt = now
    this.lastTickAt = now
    this.lastCheckAt = now
    this.versionTickInterval = setInterval(this.tick, TICK_MS)
    if (this.wokeAt !== null) this.checkPublishedVersion()
  },
  beforeDestroy() {
    clearInterval(this.versionTickInterval)
  },
  methods: {
    tick() {
      const now = Date.now()
      if (hasWoken({ lastTickAt: this.lastTickAt, now })) this.wokeAt = now
      this.lastTickAt = now
      if (
        isAfterWake({ now, wokeAt: this.wokeAt }) ||
        now - this.lastCheckAt >= CHECK_INTERVAL_MS ||
        // A clock set back would otherwise hold off checks until it caught up
        now < this.lastCheckAt
      ) {
        this.checkPublishedVersion()
      }
    }, // tick
    async checkPublishedVersion() {
      if (this.isCheckingVersion) return
      this.isCheckingVersion = true
      this.lastCheckAt = Date.now()
      try {
        const published = await fetchFresh(
          '/version.json',
          async (response) => parsePublishedVersion(await response.text())
        )
        if (!published) return
        this.publishedDeployId = published.deployId
        this.publishedForceReloadVersion = published.forceReloadVersion
        await this.reloadDisplayIfDue(published.deployId)
      } catch (e) {
        // Nothing changes, and the next check tries again
      } finally {
        this.isCheckingVersion = false
      }
    }, // checkPublishedVersion
    async reloadDisplayIfDue(deployId) {
      if (!this.isDisplayReloadDue()) return
      const attempt = readReloadAttempt()
      const count =
        attempt &&
        attempt.deployId === deployId &&
        Date.now() - attempt.at < RELOAD_ATTEMPT_RESET_MS
          ? attempt.count
          : 0
      if (count >= MAX_RELOAD_ATTEMPTS) return
      if (!(await apiAnswers())) return
      // The screen may have changed while the API answered
      if (!this.isDisplayReloadDue()) return
      // Without a record of the attempt there's nothing to stop a reload loop
      if (!writeReloadAttempt({ deployId, count: count + 1, at: Date.now() })) {
        return
      }
      this.reloadForNewVersion()
    }, // reloadDisplayIfDue
    isDisplayReloadDue() {
      return (
        this.newVersionAction === NEW_VERSION_ACTIONS.RELOAD &&
        canReloadDisplay({
          now: Date.now(),
          wokeAt: this.wokeAt,
          forceReload: isForceReload({
            running: this.runningForceReloadVersion,
            published: this.publishedForceReloadVersion,
          }),
        })
      )
    },
    reloadForNewVersion() {
      window.location.reload()
    },
  },
}
